import React, { useCallback, useEffect, useRef, useState } from "react";
import { http, inr, requestErrorMessage } from "@/lib/erp";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Tag, ListChecks } from "lucide-react";
import { toast } from "sonner";

export function PriceLists() {
  const [lists, setLists] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [editId, setEditId] = useState(null);
  const [rateFor, setRateFor] = useState(null);
  const [rates, setRates] = useState({});
  const [listsError, setListsError] = useState("");
  const [listsLoading, setListsLoading] = useState(true);
  const [productsError, setProductsError] = useState("");
  const [productsLoading, setProductsLoading] = useState(true);
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [ratesError, setRatesError] = useState("");
  const [ratesLoading, setRatesLoading] = useState(false);
  const [ratesLoaded, setRatesLoaded] = useState(false);
  const ratesRequestId = useRef(0);

  const load = useCallback(async () => {
    setListsLoading(true);
    try {
      const { data } = await http.get("/masters/price_lists");
      setLists(data);
      setListsError("");
    } catch (e) {
      setListsError(requestErrorMessage(e, "load price lists"));
    } finally {
      setListsLoading(false);
    }
  }, []);
  const loadProducts = useCallback(async () => {
    setProductsLoading(true);
    try {
      const { data } = await http.get("/products");
      setProducts(data);
      setProductsLoaded(true);
      setProductsError("");
    } catch (e) {
      setProductsError(requestErrorMessage(e, "load products"));
    } finally {
      setProductsLoading(false);
    }
  }, []);
  useEffect(() => { load(); loadProducts(); }, [load, loadProducts]);

  const save = async () => {
    if (!(form.name || "").trim()) return toast.error("Name required");
    try {
      if (editId) await http.put(`/masters/price_lists/${editId}`, form);
      else await http.post("/masters/price_lists", form);
      toast.success("Saved"); setOpen(false); await load();
    } catch (e) { toast.error(requestErrorMessage(e, "save price list")); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete price list?")) return;
    try {
      await http.delete(`/masters/price_lists/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error(requestErrorMessage(e, "delete price list")); }
  };

  const openRates = async (pl) => {
    const requestId = ++ratesRequestId.current;
    setRateFor(pl);
    setRates({});
    setRatesError("");
    setRatesLoaded(false);
    setRatesLoading(true);
    try {
      const { data } = await http.get(`/price-lists/${pl.id}/items`);
      if (requestId !== ratesRequestId.current) return;
      const m = {}; data.forEach((x) => { m[x.product_id] = x.price; }); setRates(m);
      setRatesLoaded(true);
    } catch (e) {
      if (requestId === ratesRequestId.current) setRatesError(requestErrorMessage(e, "load product rates"));
    } finally {
      if (requestId === ratesRequestId.current) setRatesLoading(false);
    }
  };
  const closeRates = () => {
    ratesRequestId.current += 1;
    setRateFor(null);
    setRatesLoading(false);
    setRatesLoaded(false);
  };
  const saveRates = async () => {
    if (!rateFor || !ratesLoaded || ratesLoading || ratesError || !productsLoaded || productsLoading || productsError) return;
    const items = Object.entries(rates).filter(([, v]) => v !== "" && v != null).map(([product_id, price]) => ({ product_id, price: +price }));
    try {
      await http.put(`/price-lists/${rateFor.id}/items`, { items });
      toast.success(`${items.length} product rates saved`); closeRates();
    } catch (e) { toast.error(requestErrorMessage(e, "save product rates")); }
  };

  return (
    <div className="space-y-4 mt-4">
      <div className="flex justify-end"><Button size="sm" onClick={() => { setForm({}); setEditId(null); setOpen(true); }} data-testid="add-pricelist-button"><Plus className="h-4 w-4 mr-1" /> New Price List</Button></div>
      {listsError && <div role="alert" className="flex items-center justify-between gap-3 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
        <span>{listsError}</span><Button size="sm" variant="outline" onClick={load}>Retry</Button>
      </div>}
      <Card className="p-0 overflow-hidden"><div className="overflow-x-auto"><table className="w-full text-sm">
        <thead className="bg-slate-50"><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground"><th className="px-4 py-3 font-medium">Price List</th><th className="px-4 py-3 font-medium">Markup / Discount %</th><th className="px-4 py-3 font-medium">Notes</th><th className="px-4 py-3 font-medium text-right">Actions</th></tr></thead>
        <tbody>
          {lists.map((pl) => (
            <tr key={pl.id} className="border-t hover:bg-slate-50" data-testid={`pricelist-row-${pl.id}`}>
              <td className="px-4 py-3 font-medium">{pl.name}</td>
              <td className="px-4 py-3">{pl.markup_pct != null && pl.markup_pct !== "" ? `${pl.markup_pct}%` : "—"}</td>
              <td className="px-4 py-3 text-muted-foreground">{pl.description || "—"}</td>
              <td className="px-4 py-3"><div className="flex items-center justify-end gap-1">
                <Button size="sm" variant="outline" onClick={() => openRates(pl)} data-testid={`pricelist-rates-${pl.id}`}><ListChecks className="h-3.5 w-3.5 mr-1" /> Rates</Button>
                <button onClick={() => { setForm({ ...pl }); setEditId(pl.id); setOpen(true); }} className="p-1.5 hover:bg-slate-100 rounded"><Pencil className="h-4 w-4 text-slate-600" /></button>
                <button onClick={() => del(pl.id)} className="p-1.5 hover:bg-rose-50 rounded"><Trash2 className="h-4 w-4 text-rose-500" /></button>
              </div></td>
            </tr>
          ))}
          {listsLoading && lists.length === 0 && !listsError && <tr><td colSpan={4} className="px-4 py-12 text-center text-muted-foreground">Loading price lists…</td></tr>}
          {!listsLoading && !listsError && lists.length === 0 && <tr><td colSpan={4} className="px-4 py-12 text-center text-muted-foreground"><Tag className="h-8 w-8 mx-auto mb-2 opacity-40" />No price lists yet</td></tr>}
        </tbody>
      </table></div></Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editId ? "Edit Price List" : "New Price List"}</DialogTitle></DialogHeader>
          <div className="grid gap-4">
            <div><Label>Name *</Label><Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="pricelist-name-input" placeholder="e.g. Wholesale" /></div>
            <div><Label>Default Markup / Discount %</Label><Input type="number" value={form.markup_pct ?? ""} onChange={(e) => setForm({ ...form, markup_pct: e.target.value })} className="mt-1.5" placeholder="e.g. -5 for 5% off" /></div>
            <div><Label>Notes</Label><Input value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="save-pricelist-button">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rateFor} onOpenChange={(v) => !v && closeRates()}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{rateFor?.name} · Per-Product Rates</DialogTitle></DialogHeader>
          <div className="text-xs text-muted-foreground">Set a specific rate per product. Blank rows fall back to the product default.</div>
          {productsError && <div role="alert" className="flex items-center justify-between gap-3 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
            <span>{productsError}</span><Button size="sm" variant="outline" onClick={loadProducts}>Retry</Button>
          </div>}
          {ratesError && <div role="alert" className="flex items-center justify-between gap-3 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
            <span>{ratesError}</span><Button size="sm" variant="outline" onClick={() => openRates(rateFor)}>Retry</Button>
          </div>}
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {products.map((p) => (
              <div key={p.id} className="flex items-center gap-3">
                <div className="flex-1 min-w-0 text-sm truncate">{p.name} <span className="text-xs text-muted-foreground">· MRP {inr(p.mrp)}</span></div>
                <Input type="number" value={rates[p.id] ?? ""} onChange={(e) => setRates({ ...rates, [p.id]: e.target.value })} className="h-9 w-28 text-right" placeholder={String(p.ptr || p.mrp || 0)} data-testid={`rate-input-${p.id}`} />
              </div>
            ))}
            {productsLoading && <div className="py-4 text-center text-sm text-muted-foreground">Loading products…</div>}
            {!productsLoading && !productsError && productsLoaded && products.length === 0 && <div className="py-4 text-center text-sm text-muted-foreground">No products available</div>}
          </div>
          <DialogFooter><Button variant="outline" onClick={closeRates}>Cancel</Button><Button onClick={saveRates} disabled={!ratesLoaded || ratesLoading || !!ratesError || !productsLoaded || productsLoading || !!productsError} data-testid="save-rates-button">Save Rates</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
